import { useSocket } from "@/contexts/SocketContext.tsx";
import type { ServerToClientThreadEvents } from "@/types/socketIo.ts";
import { useEffect, useRef } from "react";

/**
 * Hook for listening to thread:* events from the user room
 * https://socket.io/how-to/use-with-react
 */
export const useThreadSocket = <T extends keyof ServerToClientThreadEvents>(
  threadId: number | null,
  eventName: T,
  handler: ServerToClientThreadEvents[T],
) => {
  const { socket } = useSocket();

  // Store the callback in a ref - we don't want to re-register the socket listener
  // every time the callback changes, we might miss events.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // Event listener registration
  useEffect(() => {
    if (!socket) {
      return;
    }

    // Wrapper to filter down to events for only the current thread
    const listener: typeof handler = (data: Parameters<typeof handler>[0]) => {
      if (data.threadId === threadId) {
        handlerRef.current(data as any); // any cast - socket.io's types are very strict, but we have type safety in our hook interface
      }
    };

    socket.on(eventName, listener as any); // any cast - ditto

    return () => {
      // must pass the listener we passed to "on", otherwise we'd unsub all handlers for these events
      // https://socket.io/how-to/use-with-react#disconnection
      socket.off(eventName, listener as any); // any cast - ditto
    };
  }, [socket, threadId, eventName]);

  return {};
};
